import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { HelpLink } from '../admin/HelpLink';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const placeName = (j) => (j?.name === 'New York City' ? 'New York City' : (j?.name || '').replace(' (city)', '').replace(' (outside the listed cities)', ''));

// One-line staff explanation, e.g. "Tax: $86.25 — Nassau County, NY · 8.625%"
export const taxExplain = (doc) => {
  const d = doc?.tax_details;
  if (!d) return `Tax: ${money(doc?.tax)} — ${doc?.tax_reason || 'calculated before the tax rebuild (historical)'}`;
  const reason = d.reason || doc.tax_reason || '';
  if (d.status === 'pending_address') {
    return `Tax: To be confirmed — ${/address needed/i.test(reason) ? 'delivery address required' : reason.replace(/^Tax to be confirmed - /, '')}`;
  }
  if (d.status === 'calculated') return `Tax: ${money(doc.tax)} — ${placeName(d.jurisdiction)}, ${d.state || 'NY'} · ${d.rate}%`;
  if (d.status === 'exempt') {
    const cert = (reason.match(/\((ST-[\d.]+)\)/) || [])[1] || 'certificate';
    return `Tax: ${money(0)} — Exempt · ${cert} on file`;
  }
  if (d.status === 'override') return `Tax: ${money(doc.tax)} — Manual override · ${d.rate}%`;
  return `Tax: ${money(0)} — ${/collection starts/.test(reason) ? reason : `${d.state || d.destination?.state || 'This state'} collection not enabled`}`;
};

const CHIP = {
  calculated: ['Collecting', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  not_collected: ['Not collecting', 'bg-neutral-100 text-neutral-600 border-neutral-200'],
  pending_address: ['Tax to be confirmed', 'bg-amber-50 text-amber-800 border-amber-200'],
  exempt: ['Exempt', 'bg-sky-50 text-sky-700 border-sky-200'],
  override: ['Manual override', 'bg-violet-50 text-violet-700 border-violet-200'],
};

export const nexusState = (doc) => doc?.nexus_review_state || (doc?.tax_details?.nexus_review ? doc.tax_details.state : '');

export const TaxExplainLine = ({ doc, testId = 'tax-explain' }) => {
  const [label, cls] = CHIP[doc?.tax_details?.status] || ['Historical', 'bg-neutral-100 text-neutral-500 border-neutral-200'];
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={testId}>
      <span className={`px-2 py-0.5 rounded-full border font-medium ${cls}`} data-testid={`${testId}-status`}>{label}</span>
      {nexusState(doc) && <span className="px-2 py-0.5 rounded-full border font-medium bg-red-50 text-red-700 border-red-200" data-testid={`${testId}-nexus`}>Nexus review needed</span>}
      <span className="text-neutral-600" data-testid={`${testId}-text`}>{taxExplain(doc)}</span>
    </div>
  );
};

export const NexusBanner = ({ doc, testId = 'nexus-banner' }) => {
  const st = nexusState(doc);
  if (!st) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid={testId}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <p><strong>Nexus review needed ({st}).</strong> Apollo has physical activity in this state but its registration status is unresolved. Tax stays as shown until an Admin resolves it in Sales Tax. <HelpLink topic="tax-out-of-state" className="text-red-700" /></p>
    </div>
  );
};

export const TestBadge = ({ doc, testId }) => (doc?.is_test ? (
  <span className="px-2 py-0.5 text-[11px] rounded-full font-bold bg-red-600 text-white tracking-wide" title={doc.test_label} data-testid={testId || `test-badge-${doc.id}`}>
    TEST – DO NOT FULFILL / SHIP
  </span>
) : null);

const Row = ({ k, v, testId }) => <div className="flex justify-between gap-3"><span className="text-neutral-500">{k}</span><span className="text-right text-neutral-800" data-testid={testId}>{v || '—'}</span></div>;

export const ExemptionSummary = ({ ex, certNames = {}, testId = 'exemption-summary' }) => (
  <div className="text-xs space-y-1" data-testid={testId}>
    <Row k="Exempt" v={ex?.exempt ? 'Yes' : 'No'} testId={`${testId}-exempt`} />
    {ex?.exempt && <>
      <Row k="Certificate type" v={certNames[ex.certificate_type] || ex.certificate_type} />
      <Row k="Certificate on file" v={ex.certificate_on_file ? 'Yes' : 'No'} />
      <Row k="Certificate / reference no." v={ex.certificate_ref} />
      <Row k="Reason" v={ex.reason} />
    </>}
    {ex?.applied_by && <Row k="Applied by" v={`${ex.applied_by} · ${new Date(ex.applied_at).toLocaleString()}`} />}
  </div>
);
